import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, ShieldCheck, Info } from 'lucide-react';

const LAST_UPDATED = 'January 2026';

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-32 mb-12">
    <h2 className="font-display text-[26px] md:text-[32px] tracking-tight text-[#ECE9DF] mb-4">
      {title}
    </h2>
    <div className="text-[15px] text-[#C9C5B8] leading-relaxed space-y-4">
      {children}
    </div>
  </section>
);

const TOC = [
  { id: 'about', label: '1. About this policy' },
  { id: 'what', label: '2. Information we collect' },
  { id: 'how', label: '3. How we collect information' },
  { id: 'use', label: '4. How we use your information' },
  { id: 'share', label: '5. Who we share information with' },
  { id: 'meta', label: '6. Meta / Facebook Lead Ads' },
  { id: 'overseas', label: '7. Overseas disclosure' },
  { id: 'storage', label: '8. Storage & retention' },
  { id: 'security', label: '9. Security' },
  { id: 'rights', label: '10. Your rights' },
  { id: 'cookies', label: '11. Cookies & analytics' },
  { id: 'children', label: '12. Children' },
  { id: 'changes', label: '13. Changes to this policy' },
  { id: 'contact', label: '14. Contact us' },
];

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.title = t('privacy.docTitle');
    window.scrollTo(0, 0);
  }, [t]);
  const notice = t('privacy.notice');
  const noticeHeading = t('privacy.noticeHeading');
  const showNotice = i18n.language !== 'en' && notice;

  return (
    <div className="relative min-h-screen bg-[#07120F] text-[#ECE9DF]">
      <Navbar />

      {/* Hero band */}
      <section className="relative pt-40 pb-16 md:pt-48 md:pb-20 border-b border-white/5">
        <div className="max-w-[960px] mx-auto px-6 md:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-[#5FE0C8] hover:text-[#7EE9D6] transition-colors mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> {t('privacy.backHome')}
          </Link>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-9 h-9 rounded-full border border-[#5FE0C8]/40 bg-[#0E2A24] grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-[#5FE0C8]" strokeWidth={1.6} />
            </span>
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#5FE0C8]">{t('privacy.eyebrow')}</span>
          </div>
          <h1 className="font-display text-[42px] md:text-[62px] leading-[0.98] tracking-tight">
            How we handle your <span className="font-serif-i text-[#5FE0C8]">information.</span>
          </h1>
          {showNotice && (
            <div
              data-testid="privacy-language-notice"
              className="mt-7 flex items-start gap-3 rounded-xl border border-[#5FE0C8]/25 bg-[#0E2A24]/60 px-4 py-3.5 max-w-[680px]"
            >
              <Info className="w-4 h-4 text-[#5FE0C8] mt-0.5 shrink-0" strokeWidth={1.8} />
              <div className="text-[13.5px] text-[#C9C5B8] leading-relaxed">
                {noticeHeading && (
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#5FE0C8] mb-1">{noticeHeading}</div>
                )}
                {notice}
              </div>
            </div>
          )}
          <p className="mt-6 text-[15px] md:text-[16px] text-[#9FA8A4] leading-relaxed max-w-[680px]">
            Mint Property Advisory Pty Ltd (ABN 59 616 152 893) is committed to protecting your personal information. This policy explains what we collect, why, and your rights under the Australian Privacy Principles (APPs) contained in the <em>Privacy Act 1988</em> (Cth).
          </p>
          <p className="mt-4 text-[12.5px] text-[#7E8A85] tracking-wide">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      {/* Content grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1160px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* TOC */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#7E8A85] mb-4">Contents</div>
              <nav className="flex flex-col gap-2 text-[13px]">
                {TOC.map((t) => (
                  <a key={t.id} href={`#${t.id}`} className="text-[#9FA8A4] hover:text-[#5FE0C8] transition-colors leading-snug">
                    {t.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Body */}
          <article className="lg:col-span-9 max-w-[760px]">
            <Section id="about" title="1. About this policy">
              <p>
                This policy applies to all personal information collected by <strong>Mint Property Advisory Pty Ltd</strong> (<em>we</em>, <em>us</em>, <em>our</em>) through our website <a href="https://mintpropertyadvisory.com.au" className="text-[#5FE0C8] hover:underline">mintpropertyadvisory.com.au</a>, our email address, phone enquiries, lead-generation forms operated on our behalf (including Meta / Facebook / Instagram Instant Forms), and any other channels through which you interact with us.
              </p>
              <p>
                By providing your personal information to us, you consent to us collecting, holding, using and disclosing it in accordance with this policy.
              </p>
            </Section>

            <Section id="what" title="2. Information we collect">
              <p>The types of personal information we typically collect include:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Contact details:</strong> full name, email address, phone number, city / state.</li>
                <li><strong>Property and investment brief:</strong> asset class, budget range, yield target, timing, borrowing structure, and any details you voluntarily share with us about your investment mandate.</li>
                <li><strong>Correspondence:</strong> emails, messages, phone-call notes and any documents you share with us.</li>
                <li><strong>Referral / testimonial information:</strong> where you consent to be named or quoted publicly.</li>
                <li><strong>Technical information:</strong> device type, browser and general geographic region — collected only if we later enable analytics (see section 11).</li>
              </ul>
              <p>We do not knowingly collect sensitive information (health, biometric, criminal record, etc.). If you volunteer any, we will only use it for the purpose you provided it.</p>
            </Section>

            <Section id="how" title="3. How we collect information">
              <p>We collect personal information directly from you when you:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Complete the contact form on our website;</li>
                <li>Submit a Meta / Facebook / Instagram Lead Ad (Instant Form) referring to our services;</li>
                <li>Email us at <a href="mailto:info@mintpropertyadvisory.com.au" className="text-[#5FE0C8] hover:underline">info@mintpropertyadvisory.com.au</a>;</li>
                <li>Speak to us over the phone;</li>
                <li>Engage us as a client under our Gold or Platinum package agreement.</li>
              </ul>
              <p>We may also receive personal information from selling agents, brokers, valuers, lenders and other third-party stakeholders in the course of representing you in a property acquisition — always for the purpose you have engaged us to undertake.</p>
            </Section>

            <Section id="use" title="4. How we use your information">
              <p>We use personal information for the following purposes:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>To respond to your enquiry and provide you with information about our services;</li>
                <li>To provide buyer&rsquo;s advocacy services under our engagement agreement (property search, pricing guidance, due-diligence coordination, settlement management);</li>
                <li>To communicate with you about your engagement, market updates and opportunities that match your brief;</li>
                <li>To improve and personalise our website and services;</li>
                <li>To comply with our legal, regulatory and licensing obligations as an Agents Representative in Victoria;</li>
                <li>For any other purpose you specifically consent to.</li>
              </ul>
              <p>We do not sell your personal information. We do not use it for third-party direct marketing without your explicit consent.</p>
            </Section>

            <Section id="share" title="5. Who we share information with">
              <p>We only share your personal information where necessary and with parties who are bound to protect it. This may include:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Service providers</strong> we use to run our business — including our email delivery provider (<a href="https://resend.com/legal/privacy-policy" className="text-[#5FE0C8] hover:underline" target="_blank" rel="noreferrer">Resend, Inc.</a>), website and database hosting providers, and cloud storage;</li>
                <li><strong>Selling agents, vendors, solicitors, brokers, valuers, building inspectors, tenants and other stakeholders</strong> where necessary to represent you in a specific property acquisition;</li>
                <li><strong>Government, regulatory or law-enforcement bodies</strong> where we are legally required to do so (for example under the <em>Estate Agents Act 1980</em> (Vic));</li>
                <li><strong>Professional advisors</strong> (accountants, lawyers, insurers) where relevant and confidential.</li>
              </ul>
              <p>We do not disclose your personal information to any party for the purposes of unrelated marketing.</p>
            </Section>

            <Section id="meta" title="6. Meta / Facebook Lead Ads">
              <p>
                We may advertise our services through Meta Platforms Ireland Limited (<em>Meta</em>) — including Facebook and Instagram — using Lead Ads (Instant Forms). When you submit an Instant Form:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Meta collects the personal information you enter into that form (typically full name, email address, phone number and any additional questions we specify);</li>
                <li>Meta transmits that information to us so we can respond to your enquiry;</li>
                <li>Meta&rsquo;s own use of your data on their platform is governed by <a href="https://www.facebook.com/privacy/policy/" className="text-[#5FE0C8] hover:underline" target="_blank" rel="noreferrer">Meta&rsquo;s Privacy Policy</a>, over which we have no control;</li>
                <li>We only use the information you submitted for the purpose of contacting you about the service you enquired about;</li>
                <li>You may withdraw your consent at any time by emailing us at <a href="mailto:info@mintpropertyadvisory.com.au" className="text-[#5FE0C8] hover:underline">info@mintpropertyadvisory.com.au</a>.</li>
              </ul>
            </Section>

            <Section id="overseas" title="7. Overseas disclosure">
              <p>
                Some of our service providers are based outside Australia. In particular, our email delivery provider (Resend, Inc.) is based in the <strong>United States</strong> and our website and database infrastructure may be hosted in servers located in the United States or the European Union.
              </p>
              <p>
                Where we transfer personal information overseas, we take reasonable steps to ensure the recipient is bound by comparable privacy obligations to the Australian Privacy Principles.
              </p>
            </Section>

            <Section id="storage" title="8. Storage & retention">
              <p>
                We store personal information in secure cloud-hosted databases with encryption in transit and at rest. Access is restricted to personnel who require it to do their work.
              </p>
              <p>
                We retain your personal information only for as long as necessary to fulfil the purpose it was collected for, or as required by law (for example, the Estate Agents Act 1980 (Vic) requires records to be retained for seven years). When information is no longer required, we take reasonable steps to destroy or de-identify it.
              </p>
            </Section>

            <Section id="security" title="9. Security">
              <p>
                We take reasonable steps to protect the personal information we hold from misuse, interference, loss, unauthorised access, modification or disclosure. However, no method of transmission over the internet or electronic storage is 100% secure. If you believe your personal information has been compromised, please contact us immediately.
              </p>
            </Section>

            <Section id="rights" title="10. Your rights">
              <p>Under the Australian Privacy Principles, you have the right to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Access</strong> the personal information we hold about you;</li>
                <li><strong>Correct</strong> any inaccurate, out-of-date, incomplete, irrelevant or misleading information;</li>
                <li><strong>Request deletion</strong> of your personal information, subject to our legal retention obligations;</li>
                <li><strong>Opt out</strong> of any electronic marketing communications (each communication will contain an unsubscribe option);</li>
                <li><strong>Withdraw your consent</strong> at any time for future uses of your information;</li>
                <li><strong>Complain</strong> to the Office of the Australian Information Commissioner (OAIC) if you believe we have breached the APPs — see <a href="https://www.oaic.gov.au" className="text-[#5FE0C8] hover:underline" target="_blank" rel="noreferrer">oaic.gov.au</a>.</li>
              </ul>
              <p>To exercise any of these rights, email us at <a href="mailto:info@mintpropertyadvisory.com.au" className="text-[#5FE0C8] hover:underline">info@mintpropertyadvisory.com.au</a>. We will respond within a reasonable timeframe and, in most cases, within 30 days.</p>
            </Section>

            <Section id="cookies" title="11. Cookies & analytics">
              <p>
                Our website does not currently use tracking cookies or third-party analytics. If we introduce cookies or analytics tools (such as Google Analytics or Meta Pixel) in the future, we will update this policy and, where required, obtain your consent.
              </p>
              <p>
                Advertisements served on Meta platforms may use Meta&rsquo;s own cookies and pixels, which are governed by <a href="https://www.facebook.com/policies/cookies/" className="text-[#5FE0C8] hover:underline" target="_blank" rel="noreferrer">Meta&rsquo;s Cookies Policy</a>.
              </p>
            </Section>

            <Section id="children" title="12. Children">
              <p>
                Our services are directed at adults engaged in commercial property investment. We do not knowingly collect personal information from anyone under the age of 18. If you believe a child has provided us with personal information, please contact us so we can delete it.
              </p>
            </Section>

            <Section id="changes" title="13. Changes to this policy">
              <p>
                We may update this Privacy Policy from time to time to reflect changes to our practices, our services or applicable law. The most current version will always be published at this URL, with the &ldquo;Last updated&rdquo; date at the top of the page. Material changes will be brought to the attention of current clients directly.
              </p>
            </Section>

            <Section id="contact" title="14. Contact us">
              <p>Any question, request or complaint about this policy or your personal information should be directed to:</p>
              <div className="mt-2 panel-card rounded-2xl p-6 not-prose">
                <div className="font-display text-[18px] text-[#ECE9DF] mb-2">Mint Property Advisory Pty Ltd</div>
                <div className="text-[13.5px] text-[#9FA8A4] leading-relaxed">
                  ABN 59 616 152 893 · Agents Representative (VIC)<br />
                  Email: <a href="mailto:info@mintpropertyadvisory.com.au" className="text-[#5FE0C8] hover:underline">info@mintpropertyadvisory.com.au</a><br />
                  Web: <a href="https://mintpropertyadvisory.com.au" className="text-[#5FE0C8] hover:underline">mintpropertyadvisory.com.au</a>
                </div>
              </div>
              <p className="text-[13px] text-[#7E8A85] mt-6">
                If you are not satisfied with our response, you may lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at <a href="https://www.oaic.gov.au" className="text-[#5FE0C8] hover:underline" target="_blank" rel="noreferrer">oaic.gov.au</a>.
              </p>
            </Section>
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
